"use client"

/* eslint-disable functional/immutable-data -- This animation owns only its measured DOM styles and disposable Three.js controller. */
import { type ReactNode, useEffect, useRef, useState } from "react"

import type { createAtelierScene } from "./atelier-scene"
import styles from "./Top.module.css"

type SceneController = ReturnType<typeof createAtelierScene>

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)"
// モバイル幅では three.js（転送 137KiB / スクリプト実行 2 秒超）の負荷が
// TBT と LCP に直撃するため、3D は読み込まず CSS の静止モチーフだけを見せる。
// 写真の浮遊・整列は DOM だけで動くのでモバイルでも続ける。
const WIDE_ENOUGH = "(min-width: 768px)"

/** WebGL シーンを動かす価値があるデバイスか。 */
function shouldRenderScene(): boolean {
  if (window.matchMedia(REDUCED_MOTION).matches) return false
  const connection = (
    navigator as Navigator & { connection?: { saveData?: boolean } }
  ).connection
  if (connection?.saveData) return false
  return window.matchMedia(WIDE_ENOUGH).matches
}

/** メインスレッドが空くまで待ってから実行する（未対応環境は setTimeout）。 */
function idle(run: () => void): void {
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(run, { timeout: 3000 })
    return
  }
  window.setTimeout(run, 1000)
}
interface PhotoPosition {
  element: HTMLElement
  caption: HTMLElement | null
  width: number
  height: number
  angle: number
  dx: number
  dy: number
  targetWidth: number
  targetHeight: number
  hasTarget: boolean
}

export function AtelierMotion({ children }: { children: ReactNode }) {
  const rootRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLDivElement>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    const root = rootRef.current
    const host = canvasRef.current
    const hero = root?.querySelector<HTMLElement>("[data-atelier-hero]")
    if (!root || !host || !hero) return
    const reduced = window.matchMedia(REDUCED_MOTION)
    const wideEnough = window.matchMedia(WIDE_ENOUGH)
    let disposed = false
    let visible = false
    let loading = false
    let failed = false
    let generation = 0
    let scene: SceneController | null = null
    let frame = 0
    let previous = 0
    let elapsed = 0
    let desktop = true
    let heroInView = true
    let measured = false
    let distance = 1
    let targetProgress = 0
    let progress = 0
    let pointerX = 0
    let pointerY = 0
    let currentX = 0
    let currentY = 0
    let positions: PhotoPosition[] = []

    const onScroll = () => {
      heroInView = hero.getBoundingClientRect().bottom > 0
      targetProgress = desktop
        ? Math.min(
            1,
            Math.max(0, (64 - root.getBoundingClientRect().top) / distance),
          )
        : 0
      sync()
    }
    const measure = () => {
      if (disposed) return
      const bounds = root.getBoundingClientRect()
      desktop = bounds.width >= 768
      const shells = [
        ...root.querySelectorAll<HTMLElement>("[data-atelier-photo]"),
      ]
      positions = shells.flatMap((shell) => {
        const element = shell.querySelector<HTMLElement>("[data-atelier-float]")
        const key = shell.dataset.atelierPhoto
        if (!element || !key) return []
        const start = shell.getBoundingClientRect()
        const target = root
          .querySelector<HTMLElement>(`[data-atelier-target="${key}"]`)
          ?.getBoundingClientRect()
        return [
          {
            element,
            caption: element.querySelector<HTMLElement>("span"),
            width: start.width,
            height: start.height,
            angle: Number(shell.dataset.angle ?? 0),
            dx: target ? target.left - start.left : 0,
            dy: target ? target.top - start.top : 0,
            targetWidth: target?.width ?? start.width,
            targetHeight: target?.height ?? start.height,
            hasTarget: Boolean(target),
          },
        ]
      })
      const target = root
        .querySelector<HTMLElement>("[data-atelier-target]")
        ?.getBoundingClientRect()
      distance = target
        ? Math.max(
            1,
            target.top - bounds.top - Math.min(220, window.innerHeight * 0.24),
          )
        : hero.offsetHeight
      root.style.setProperty("--atelier-height", `${hero.offsetHeight}px`)
      root.dataset.enhanced = String(desktop && !reduced.matches)
      scene?.resize(bounds.width, hero.offsetHeight)
      onScroll()
      if (!measured) {
        progress = targetProgress
        measured = true
      }
    }
    const draw = (now: number) => {
      frame = 0
      if (
        disposed ||
        !visible ||
        (!desktop && !heroInView) ||
        reduced.matches ||
        document.hidden
      )
        return
      if (now - previous >= 1000 / 30) {
        elapsed += previous ? Math.min(now - previous, 70) : 0
        previous = now
        progress += (targetProgress - progress) * 0.16
        if (Math.abs(targetProgress - progress) < 0.001)
          progress = targetProgress
        currentX += (pointerX - currentX) * 0.07
        currentY += (pointerY - currentY) * 0.07
        const p = desktop ? progress : 0
        positions.forEach((item, index) => {
          const drift =
            Math.sin(elapsed * 0.00055 + index * 1.5) * (desktop ? 4 : 2)
          const x = item.dx * p + currentX * (index + 1) * 10 * (1 - p)
          const y = item.dy * p + (currentY * (index + 1) * 6 + drift) * (1 - p)
          item.element.style.width = `${item.width + (item.targetWidth - item.width) * p}px`
          item.element.style.height = `${item.height + (item.targetHeight - item.height) * p}px`
          item.element.style.transform = `translate3d(${x}px, ${y}px, 0) perspective(1200px) rotateX(${currentY * -6 * (1 - p)}deg) rotateY(${currentX * 8 * (1 - p)}deg) rotate(${item.angle * (1 - p)}deg)`
          item.element.style.opacity = String(
            item.hasTarget ? Math.min(1, (1 - p) * 10) : 1 - p,
          )
          item.element.style.visibility = p >= 0.999 ? "hidden" : "visible"
          item.element.style.padding = `${(desktop ? 10 : 6) * (1 - p)}px ${(desktop ? 10 : 6) * (1 - p)}px 0`
          const caption = item.caption
          if (caption) {
            caption.style.opacity = String(1 - p)
            caption.style.minHeight = `${(desktop ? 34 : 27) * (1 - p)}px`
            caption.style.height = `${(desktop ? 34 : 27) * (1 - p)}px`
            caption.style.overflow = "hidden"
          }
        })
        root.style.setProperty(
          "--atelier-target-opacity",
          String(desktop ? Math.max(0, (p - 0.9) * 10) : 1),
        )
        scene?.render(elapsed, currentX, currentY, p)
      }
      if (progress < 0.999 || targetProgress !== progress)
        frame = requestAnimationFrame(draw)
    }
    function sync() {
      if (frame) cancelAnimationFrame(frame)
      frame = 0
      if (
        !disposed &&
        visible &&
        (desktop || heroInView) &&
        !reduced.matches &&
        !document.hidden
      )
        frame = requestAnimationFrame(draw)
      else previous = 0
    }
    const startScene = async () => {
      if (
        disposed ||
        !visible ||
        !shouldRenderScene() ||
        loading ||
        failed ||
        scene
      )
        return
      loading = true
      const current = generation
      try {
        const { createAtelierScene } = await import("./atelier-scene")
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Unmount or preference changes may occur during the import.
        if (disposed || current !== generation || !shouldRenderScene()) return
        scene = createAtelierScene(host, () => setReady(false))
        scene.resize(root.clientWidth, hero.offsetHeight)
        scene.render(elapsed, currentX, currentY, desktop ? progress : 0)
        setReady(true)
        sync()
      } catch {
        failed = true
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Unmount may occur during the import.
        if (!disposed) setReady(false)
      } finally {
        loading = false
        if (current !== generation) void startScene()
      }
    }
    // three.js の取得・実行は LCP を過ぎたアイドル時間まで遅らせる。
    const startSceneWhenIdle = () => {
      idle(() => {
        void startScene()
      })
    }
    const onPointer = (event: PointerEvent) => {
      if (event.pointerType !== "mouse") return
      const bounds = hero.getBoundingClientRect()
      pointerX = (event.clientX - bounds.left) / bounds.width - 0.5
      pointerY = (event.clientY - bounds.top) / bounds.height - 0.5
      sync()
    }
    const onLeave = () => {
      pointerX = 0
      pointerY = 0
      sync()
    }
    const onPreference = () => {
      generation += 1
      scene?.dispose()
      scene = null
      setReady(false)
      progress = 0
      if (reduced.matches) {
        positions.forEach(({ element, caption }) => {
          element.removeAttribute("style")
          caption?.removeAttribute("style")
        })
        root.style.removeProperty("--atelier-target-opacity")
      }
      measure()
      startSceneWhenIdle()
      sync()
    }
    const observer = new IntersectionObserver(([entry]) => {
      visible = Boolean(entry?.isIntersecting)
      if (visible) {
        measure()
        startSceneWhenIdle()
      }
      sync()
    })
    observer.observe(root)
    const resizeObserver = new ResizeObserver(measure)
    resizeObserver.observe(hero)
    const business = root.querySelector("#business")
    if (business) resizeObserver.observe(business)
    hero.addEventListener("pointermove", onPointer)
    hero.addEventListener("pointerleave", onLeave)
    window.addEventListener("scroll", onScroll, { passive: true })
    document.addEventListener("visibilitychange", sync)
    reduced.addEventListener("change", onPreference)
    wideEnough.addEventListener("change", onPreference)
    measure()
    return () => {
      disposed = true
      generation += 1
      sync()
      observer.disconnect()
      resizeObserver.disconnect()
      hero.removeEventListener("pointermove", onPointer)
      hero.removeEventListener("pointerleave", onLeave)
      window.removeEventListener("scroll", onScroll)
      document.removeEventListener("visibilitychange", sync)
      reduced.removeEventListener("change", onPreference)
      wideEnough.removeEventListener("change", onPreference)
      scene?.dispose()
    }
  }, [])

  return (
    <div ref={rootRef} className={styles.atelier} data-three-ready={ready}>
      <div className={styles.fallbackShapes} aria-hidden="true">
        <i />
        <i />
        <i />
      </div>
      <div className={styles.canvas} ref={canvasRef} aria-hidden="true" />
      {children}
    </div>
  )
}
